import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';

import { DSelect, HDatePicker, HInput, HookForm } from '../../../validation/HForm';
import AppDatePicker from '../DateRangePicker/AppDatePicker';
import { ReportFilter } from './ReportFilter';
import { userService } from '../../../services/user-service';
import { cashBoxService } from '../../../services/cashbox-service';
import { reportService } from '../../../services/report-service';
import { authService } from '../../../services/auth-service';
import { reportDateRangeScheme, reportScheme } from '../../../validation/scheme/reportScheme';
import { messages } from '../../../helpers/messages';
import { Message } from '..';
import Loader from './Loader';
import { listRoles, reportType, viewReports } from '../../../helpers/data-constants';
import { calculationAccess } from '../../../helpers/common';

import CrossIcon from '../../../assets/icons/cross.svg';
import {getPrintedForm} from '../../../redux/actions/contractAction';
import { getClientFacets } from '../../../redux/actions/searchAction';
import { getBranches } from '../../../redux/actions/handbookAction';
import { getAvailableCashboxes } from '../../../redux/actions/dashboardAction';
import {useDispatch, useSelector} from 'react-redux';
import _debounce from "lodash/debounce";

const accessForDepartments = [
  listRoles.ACCOUNTANT,
  listRoles.ADMIN,
  listRoles.DIRECTOR,
  listRoles.MARKETER,
];

const defaultFormState = {
  branchIdFilter: [], 
  cashBoxIdFilter: []
};

export const LayoutReport = ({ open, onClose, dateRange, title, type, documentType }) => {
  const [lastReport, setLastReport] = useState(undefined);
  const [cashBoxes, setCashBoxes] = useState(undefined);
  const [selectedCashBox, setSelectedCashBox] = useState(undefined);
  const [loading, setLoading] = useState(() => true);
  const [loadingCasboxes, setLoadingCasboxes] = useState(true);
  const [documentNumber, setDocumentNumber] = useState(1);
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [datesError, setDatesError] = useState(null);
  const [error, setError] = useState('');
  const [errorCashBox, setErrorCashbox] = useState('');
  const dispatch = useDispatch()
  const { accessFeatures } = useSelector(({ user }) => user);
  const {remotelyReports, remoteCashBoxId, remoteUserId} = useSelector(({remotelyReports}) => remotelyReports)
  const { clientFacetData } = useSelector(({ search }) => search);
  const {cashboxes} = useSelector(({dashboard}) => dashboard);
  const isNonCashOperationCrediton = accessFeatures['non_cash.operations.type'] && type === 'CASH_TURNOVER'
  const isClientReportFeature = accessFeatures['clients.report']
  const isClientReport = type === viewReports.CLIENTS_REPORT
  const isOperationReport = type === viewReports.OPERATIONS_REPORT
  const isClientAndOperationsReport = isClientReport || isOperationReport
  const isRenderReportFilter = isClientReportFeature && isClientAndOperationsReport
  let form = {}
  const cashBoxesClient = cashboxes?.map(({ id, name }) => ({ label: name, value: id })) || [];
  const isAccessDepartments = !calculationAccess(accessForDepartments);

  const resetError = () => setError('');

  const getCashBoxes = (e, firstLaunch) => {
    setLoadingCasboxes(true);
    return cashBoxService.typeaheadCashBox(e || '', isAccessDepartments).then(({ data }) => {
      const activeCashBoxId = parseInt(authService.getActiveCashBoxId().value);

      let options = data.cashBoxes.map((item) => {
        if (item.id === activeCashBoxId && firstLaunch) {
          setSelectedCashBox({ label: item?.name, value: item?.id });
        }
        return item && { label: item?.name, value: item?.id };
      });
      setCashBoxes(options);
      setLoadingCasboxes(false);
    });
  };

  const getCurrentCashBox = () => {
    const activeCashBoxId = authService.getActiveCashBoxId().value;
    return cashBoxService.getCashBox(activeCashBoxId).then(({ data }) => {
      return data.cashBox.name;
    });
  };

  const getLastReport = useCallback(() => {
    setLoading(true);
    if (!selectedCashBox) {
      return;
    }
    const cashBoxID = (remotelyReports && remoteCashBoxId && remoteUserId) ? remoteCashBoxId?.value : selectedCashBox?.value

    return reportService
      .getLastReport(type, cashBoxID)
      .then(({ data }) => {
        if (data.id !== null && data.documentNumber !== null) {
          if (data.userId) {
            return userService
              .listUsersByIds(data.userId)
              .then((userResponse) => {
                let users = userResponse.data.users;
                if (users && users.length > 0) {
                  data.user = users[0];
                }
                setLastReport(data);
                let nextNumber =
                  data && data.documentNumber ? parseInt(data.documentNumber) + 1 : 1;
                setLoading(false);
                resetError();
                setDocumentNumber(nextNumber);
              })
              .catch((err) => {
                if (err.response) {
                  let errorData = err.response.data;
                  let commonError = messages['0'];
                  if (errorData.errorCode) {
                    commonError = messages[errorData.errorCode];
                  }
                  setLoading(false);
                  setError(commonError);
                }
              });
          } else {
            setLastReport(data);
          }
        } else {
          setLastReport(undefined);
        }
        setLoading(false);
        let nextNumber = data && data.documentNumber ? parseInt(data.documentNumber) + 1 : 1;
        resetError();
        setDocumentNumber(nextNumber);
      })
      .catch((err) => {
        if (err.response) {
          let errorData = err.response.data;
          let commonError = messages['0'];
          if (errorData.errorCode) {
            commonError = messages[errorData.errorCode];
          }
          setLoading(false);
          setError(commonError);
        }
      });
  }, [selectedCashBox, type]);

  useEffect(() => {
    if (isClientAndOperationsReport) {
      setLoading(false);
      return;
    }
    if (open) {
      getLastReport();
    }
  }, [selectedCashBox, open, isClientAndOperationsReport, getLastReport, dispatch]);

  useEffect(() => {
    getCurrentCashBox()
      .then((name) => {
        setErrorCashbox('');
        getCashBoxes(name, true);
      })
      .catch((err) => {
        if (err.response) {
          let errorData = err.response.data;
          let commonError = messages['0'];
          if (errorData.errorCode) {
            commonError = messages[errorData.errorCode];
          }
          setErrorCashbox(commonError);
        }
      });
      dispatch(getClientFacets());
  }, []);

  const generateClientReportPayload = (data) => {
    const { branchIdFilter, clientStatus, cashBoxIdFilter, allBranches } = form.getValues();
    const clientReportPayload = {
      ...data,
      branchIdFilter: branchIdFilter?.map(branch => branch.value),
      clientStatus: clientStatus?.value,
      cashBoxId: selectedCashBox.value,
      cashBoxIdFilter: allBranches ? cashBoxesClient.map(cashbox => cashbox.value) : cashBoxIdFilter?.map(cashbox => cashbox.value),
      documentNumber: undefined,
    }
    return clientReportPayload
  }

  const generateOperationReportPayload = (data) => {
    const { branchIdFilter, cashBoxIdFilter, allBranches } = form.getValues();
    return {
      ...data,
      branchIdFilter: allBranches ? [] : branchIdFilter?.map(branch => branch.value),
      cashBoxIdFilter: allBranches ? [] : cashBoxIdFilter?.map(cashbox => cashbox.value),
      documentNumber: undefined,
    };
  };

  const getReportPayload = (data, entity) => {
    if (isClientReport) {
      return generateClientReportPayload(data);
    }
    if (isOperationReport) {
      return generateOperationReportPayload(data);
    }
    if (isNonCashOperationCrediton) {
      return { ...data, cashType: entity.cashType.value } 
    }
    return data;
  };

  const onSubmit = (entity) => {
    const validationError = validateFormData();
    if (validationError) {
      return;
    }
    let data = {
      cashBoxId: (remotelyReports && remoteCashBoxId && remoteUserId) ? remoteCashBoxId?.value : entity.departments?.value,
      documentNumber: entity.documentNumber + '',
    };
    if( remotelyReports && remoteCashBoxId && remoteUserId) {
      data = {...data, remoteUserId: remoteUserId.value}
    }
    let filename = 'exp_' + data.documentNumber + '_';
    if (dateRange) {
      data.dateFrom = startDate.format('yyyy-MM-DD');
      data.dateTo = endDate.format('yyyy-MM-DD');
      filename += data.dateFrom + '_' + data.dateTo + '.' + (documentType || 'pdf');
    } else {
      data.date = moment(entity.date).format('yyyy-MM-DD');
      filename += moment(data.date).format('DD.MM.YYYY') + '.' + (documentType || 'pdf');
    }
    if (!accessFeatures["cashboxNameTypeOne"] && type === 'CASH_TURNOVER') {
      dispatch(getPrintedForm(data, filename,  'CASH_TURNOVER_XLSX', 'xlsx'))
      dispatch(getPrintedForm(data, filename,  'CASH_TURNOVER_PDF','pdf'))

    } else {
      const payload = isClientAndOperationsReport || isNonCashOperationCrediton ? getReportPayload(data, entity) : data
      filename = isClientAndOperationsReport ? data.dateFrom + '_' + data.dateTo + '.' + (documentType || 'xlsx') : filename;
      dispatch(getPrintedForm(payload, filename, type, documentType))
    }
    onClose();  
  };

  const handleCashBoxChange = (i) => {
    setSelectedCashBox(i);
  };
  const debouncedGetCashBoxes = _debounce(getCashBoxes, 250)

  const handleOptions = (e, action) => {
    if (action.action === 'input-change') debouncedGetCashBoxes(e)
  };

  const changeDataPicker = (values) => {
    setDatesError(null);
    setEndDate(values.endDate);
    setStartDate(values.startDate);
  };

  const handleClose = () => {
    onClose();
  };

  const handleOnChange = ({ target }) => {
    setDocumentNumber(target.value);
  };

  const validateFormData = () => {
    const { branchIdFilter, cashBoxIdFilter, allBranches } = form.getValues();
    
    const validationMessages = {
      clientReport: 'Необходимо выбрать Филиал или Департамент',
      startDate: 'Дата начала обязательное поле',
      endDate: 'Дата окончания обязательное поле'
    };
  
    if (isClientAndOperationsReport && !branchIdFilter.length && !cashBoxIdFilter.length && !allBranches) {
      setError(validationMessages.clientReport);
      return true;
    } 
    if (!startDate) {
      setDatesError(validationMessages.startDate);
      return true;
    } 
    if (!endDate) {
      setDatesError(validationMessages.endDate);
      return true;
    } 
    return false;
  };

  useEffect(() => {
    clientFacetData.branches && dispatch(getBranches(clientFacetData.branches.length, 1));
    dispatch(getAvailableCashboxes());
  }, [clientFacetData.branches, dispatch]);

  const renderDepartmentSelect = () => {
    const isHidden = type === 'HIGH_RISK' || isClientAndOperationsReport;
    const isDisabled = remotelyReports && remoteCashBoxId && remoteUserId;
    const defaultValue = isDisabled ? remoteCashBoxId : selectedCashBox;
  
    return (
      <div className={isHidden ? 'form-hidden' : 'form-row'}>
        <div className="form-row-item">
          <div className="form-element">
            <label className="form-element-label" htmlFor="">
              Департамент
            </label>
            <div className="form-element-data combobox">
              <DSelect
                h="true"
                name="departments"
                isLoading={loadingCasboxes}
                options={cashBoxes}
                handleOptions={handleOptions}
                handleOnChange={handleCashBoxChange}
                placeholder="Выберите"
                defaultValue={defaultValue}
                isDisabled={isDisabled}
              />
            </div>
          </div>
        </div>
        {isNonCashOperationCrediton && 
          <div className="form-row-item d-flex align-center">
              <div className="form-element-data  combobox">
                <DSelect h
                  name="cashType"
                  options={reportType}
                  placeholder="Выберите форму оплаты"
                  defaultValue={reportType[0]}
                />
              </div>
            </div>}
      </div>
    );
  };

  return (
    <>
      {(error || errorCashBox) && <Message type="error" text={error || errorCashBox} />}
      <HookForm
        onSubmit={onSubmit}
        validationSchema={dateRange ? reportDateRangeScheme(type) : reportScheme(isNonCashOperationCrediton)}
        defaultValues={defaultFormState}
        predef={(methods) => (form = methods)}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="button-icontrigger modal-closetrigger" onClick={handleClose}>
            <span className="icon">
              <CrossIcon />
            </span>
          </button>
        </div>
        {loading ? (
          <Loader />
        ) : (
          <div className="modal-body">
            <div>
              <fieldset>
                <div className="d-flex flex-start align-baseline">
                  {lastReport && (
                    <span className="modal-contract-details">
                      <big>{`Предыдущий №${lastReport.documentNumber} от
           ${moment(lastReport.date).format('DD.MM.YYYY')}${
                        lastReport.user ? ', ' + lastReport.user.fullName : ''
                      }`}</big>
                    </span>
                  )}
                </div>
              </fieldset>
              <fieldset>
                {isRenderReportFilter ? <ReportFilter
                  h="true"
                  type={type}
                  cashBoxesClient={cashBoxesClient}
                  resetError={resetError}
                /> 
                : renderDepartmentSelect()}

                <div className="form-row">
                  <div className="form-row-item">
                    <div className="form-element">
                      <label className="form-element-label" htmlFor="#">
                        Дата
                      </label>
                      <div className="form-element-data date">
                        {dateRange ? (
                          <AppDatePicker
                            coverClass="form-element"
                            startDate={startDate}
                            endDate={endDate}
                            error={datesError}
                            onChange={changeDataPicker}
                            resetPropsDate={changeDataPicker}
                          />
                        ) : (
                          <HDatePicker
                            h="true"
                            className="input"
                            name="date"
                            id="date"
                            defaultValue={moment().format('YYYY-MM-DD')}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {!isClientAndOperationsReport && <div className={type === 'HIGH_RISK' || type === 'DATABASE_UPLOAD' ? 'form-hidden' : 'form-row-item'}>
                    <div className="form-element">
                      <label className="form-element-label" htmlFor="#">
                        Номер документа
                      </label>
                      <div className="form-element-data combobox-lg">
                        <HInput
                          h="true"
                          className="input"
                          type="text"
                          name="documentNumber"
                          id="documentNumber"
                          value={documentNumber}
                          onChange={handleOnChange}
                        />
                      </div>
                    </div>
                  </div>}
                </div>
              </fieldset>
            </div>
          </div>
        )}
        <div className="modal-footer">
          <button className="button">Сохранить</button>
          <button className="button-light" onClick={handleClose}>
            Отменить
          </button>
        </div>
      </HookForm>
    </>
  );
};
